import { Event } from "event-system";
import { Ticker } from "pixi.js";
import { AnimationAction, AnimationClip, AnimationMixer, SkinnedMesh } from "three";
import { AnimationParsingUtils } from "../../../../../../three/AnimationParsingUtils";
import { WeaponType } from "../../../../models/data/WeaponType";
import { IPlayerAnimationConfiguration } from "./configurations/IPlayerAnimationConfiguration";
import configuration from "./configurations/configuration.json";

export class CharacterAnimationController {
    public readonly onClipEnd: Event<CharacterAnimationState>;
    public readonly onAnimationProgress: Event<CharacterAnimationState, number>;

    private readonly animationMixer: AnimationMixer;

    private idle: AnimationAction[];
    private death: AnimationAction[];
    private hit: AnimationAction;
    private throw: AnimationAction;
    private throwBoomerang: AnimationAction;
    private victory: AnimationAction;
    private defeat: AnimationAction;

    private currentAnimationState: CharacterAnimationState;
    private currentAnimationAction: AnimationAction;

    private animationsConfiguration: Record<string, IPlayerAnimationConfiguration>;

    public constructor(
        characterMesh: SkinnedMesh,
        animations: AnimationClip[]
    ) {
        this.onClipEnd = new Event();
        this.onAnimationProgress = new Event();
        this.animationMixer = new AnimationMixer(characterMesh);
        this.animationsConfiguration = configuration as Record<string, IPlayerAnimationConfiguration>;

        this.parsePlayerAnimations(animations);

        this.handleAnimationFinished = this.handleAnimationFinished.bind(this);

        this.animationMixer.addEventListener("finished", this.handleAnimationFinished);
        Ticker.shared.add(this.update, this);
    }

    public dispose(): void {
        Ticker.shared.remove(this.update, this);
        this.animationMixer.removeEventListener("finished", this.handleAnimationFinished);
    }

    public transitToState(next: CharacterAnimationState): void {
        this.currentAnimationState = next;
        let nextAction: AnimationAction;
        switch (this.currentAnimationState) {
            case CharacterAnimationState.Death:
                nextAction = this.playDeath();
                break;
            case CharacterAnimationState.Defeat:
                nextAction = this.playDefeat();
                break;
            case CharacterAnimationState.Hit:
                nextAction = this.playHit();
                break;
            case CharacterAnimationState.Idle:
                nextAction = this.playIdle();
                break;
            case CharacterAnimationState.Throw:
                nextAction = this.playThrow();
                break;
            case CharacterAnimationState.ThrowBoomerang:
                nextAction = this.playThrowBoomerang();
                break;
            case CharacterAnimationState.Victory:
                nextAction = this.playVictory();
                break;
        }
        nextAction.play();
        if (this.currentAnimationAction?.isRunning()) {
            if (this.currentAnimationAction.getClip().name != nextAction.getClip().name) {
                this.currentAnimationAction.crossFadeTo(nextAction, 0.2);
            }
        } else {
            this.currentAnimationAction?.crossFadeTo(nextAction, 0.2);
        }
        this.currentAnimationAction = nextAction;
    }

    public getWeaponThrowState(weapon: WeaponType): CharacterAnimationState {
        switch (weapon) {
            case WeaponType.Boomerang:
                return CharacterAnimationState.ThrowBoomerang;
            default:
                return CharacterAnimationState.Throw;
        }
    }

    private parsePlayerAnimations(animations: AnimationClip[]): void {
        this.idle = AnimationParsingUtils.animationActionByCondition(this.animationMixer, animations, (clip) => clip.name.startsWith("Idle"));
        this.idle.forEach(action => AnimationParsingUtils.applyConfigurationToAction(action, this.animationsConfiguration[action.getClip().name]));
        if (this.idle == null || this.idle.length == 0) {
            throw new Error("Character has no Idle animations configured. Check model you importing.");
        }

        this.hit = AnimationParsingUtils.animationActionByName(this.animationMixer, animations, "Hit1 ");
        AnimationParsingUtils.applyConfigurationToAction(this.hit, this.animationsConfiguration[this.hit.getClip().name]);
        if (this.hit == null) {
            throw new Error("Character has no Hit animations configured. Check model you importing.");
        }

        this.death = AnimationParsingUtils.animationActionByCondition(this.animationMixer, animations, (clip) => clip.name.startsWith("Death"));
        this.death.forEach(action => AnimationParsingUtils.applyConfigurationToAction(action, this.animationsConfiguration[action.getClip().name]));
        if (this.death == null || this.death.length == 0) {
            throw new Error("Character has no Death animations configured. Check model you importing.");
        }

        this.throw = AnimationParsingUtils.animationActionByName(this.animationMixer, animations, "Throw");
        AnimationParsingUtils.applyConfigurationToAction(this.throw, this.animationsConfiguration[this.throw.getClip().name]);
        if (this.throw == null) {
            throw new Error("Character has no Throw animation configured. Check model you importing.");
        }

        this.throwBoomerang = AnimationParsingUtils.animationActionByName(this.animationMixer, animations, "Throw_boomerang");
        AnimationParsingUtils.applyConfigurationToAction(this.throwBoomerang, this.animationsConfiguration[this.throwBoomerang.getClip().name]);
        if (this.throwBoomerang == null) {
            throw new Error("Character has no Throw_boomerang animation configured. Check model you importing.");
        }

        this.victory = AnimationParsingUtils.animationActionByName(this.animationMixer, animations, "Victory");
        AnimationParsingUtils.applyConfigurationToAction(this.victory, this.animationsConfiguration[this.victory.getClip().name]);
        if (this.victory == null) {
            throw new Error("Character has no Victory animation configured. Check model you importing.");
        }

        this.defeat = AnimationParsingUtils.animationActionByName(this.animationMixer, animations, "Defeat2");
        this.death.forEach(action => AnimationParsingUtils.applyConfigurationToAction(action, this.animationsConfiguration[action.getClip().name]));
        if (this.defeat == null) {
            throw new Error("Character has no Defeat animations configured. Check model you importing.");
        }
    }

    private update(dt: number): void {
        this.animationMixer.update(dt / 50);
        if (this.currentAnimationAction?.isRunning()) {
            const clip = this.currentAnimationAction.getClip();
            this.onAnimationProgress.emit(this.currentAnimationState, this.currentAnimationAction.time / clip.duration);
        }
    }

    private handleAnimationFinished(eventData: { action: AnimationAction, direction: number }): void {
        this.onClipEnd.emit(this.currentAnimationState);
    }

    private playHit(): AnimationAction {
        return this.hit.reset();
    }

    private playDeath(): AnimationAction {
        return this.death.random().reset();
    }

    private playDefeat(): AnimationAction {
        return this.defeat.reset();
    }

    private playIdle(): AnimationAction {
        return this.idle.random().reset();
    }

    private playThrow(): AnimationAction {
        return this.throw.reset();
    }

    private playThrowBoomerang(): AnimationAction {
        return this.throwBoomerang.reset();
    }

    private playVictory(): AnimationAction {
        return this.victory.reset();
    }
}

export enum CharacterAnimationState {
    Victory = "Victory",
    Idle = "Idle",
    Throw = "Throw",
    ThrowBoomerang = "ThrowBoomerang",
    Death = "Death",
    Defeat = "Defeat",
    Hit = "Hit"
}